/**
 * KORERIG product experience
 *
 * Eén gedeelde koopstatus (aantal + kleur per vest) die door alle panelen op de
 * pagina gedeeld wordt: de hero, de kleursectie en de afsluitende koopsectie.
 * Shopify blijft de bron van waarheid voor prijs, varianten, voorraad en cart.
 */

import { CartAddEvent, CartErrorEvent } from '@theme/events';

/* --------------------------------------------------------------- helpers -- */

/**
 * Formatteert centen volgens het money_format van de shop.
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
  const format = window.KRData?.moneyFormat || '£{{amount}}';
  const value = Number(cents || 0) / 100;

  /**
   * @param {number} precision
   * @param {string} thousands
   * @param {string} decimal
   */
  const build = (precision, thousands, decimal) => {
    const fixed = value.toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const grouped = (whole ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return precision > 0 ? `${grouped}${decimal}${fraction}` : grouped;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name) => {
    switch (name) {
      case 'amount':
        return build(2, ',', '.');
      case 'amount_no_decimals':
        return build(0, ',', '.');
      case 'amount_with_comma_separator':
        return build(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return build(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return build(2, "'", '.');
      case 'amount_no_decimals_with_space_separator':
        return build(0, ' ', ',');
      case 'amount_with_space_separator':
        return build(2, ' ', ',');
      default:
        return build(2, ',', '.');
    }
  });
}

/**
 * Het theme declareert `Theme` met const in een klassiek script. Zo'n binding
 * komt in de globale lexicale scope en dus NIET op window. Bare `Theme` werkt
 * wel, maar gooit als het script ontbreekt — vandaar de typeof-guard.
 * @returns {any}
 */
function themeGlobal() {
  // eslint-disable-next-line no-undef
  if (typeof Theme !== 'undefined') return Theme;
  return window.Theme || null;
}

/** @returns {boolean} */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** @returns {Array<Object>} */
function variants() {
  return window.KRData?.variants || [];
}

/**
 * @param {string|number} id
 * @returns {Object|undefined}
 */
function variantById(id) {
  return variants().find((variant) => String(variant.id) === String(id));
}

/** @returns {Object|undefined} Eerste leverbare variant, anders de eerste. */
function defaultVariant() {
  return variants().find((variant) => variant.available) || variants()[0];
}

/* ----------------------------------------------------------------- store -- */

/**
 * Gedeelde koopstatus. Alle panelen lezen en schrijven hier, zodat er maar één
 * variantlogica op de pagina bestaat.
 */
const store = {
  /** @type {number} */
  quantity: 1,
  /** @type {Array<string>} Variant-ID per vest, lengte === quantity */
  selection: [],
  /** @type {Set<(state: {quantity: number, selection: string[]}) => void>} */
  listeners: new Set(),

  init() {
    const first = defaultVariant();
    if (first && this.selection.length === 0) {
      this.selection = [String(first.id)];
    }
  },

  /** @param {(state: {quantity: number, selection: string[]}) => void} listener */
  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  },

  get state() {
    return { quantity: this.quantity, selection: [...this.selection] };
  },

  emit() {
    for (const listener of this.listeners) listener(this.state);
  },

  /** @param {number} quantity */
  setQuantity(quantity) {
    const next = Math.min(3, Math.max(1, Number(quantity) || 1));
    if (next === this.quantity) return;

    this.quantity = next;

    // Vul aan of kort in, met behoud van wat de klant al gekozen heeft.
    const fallback = String(this.selection[0] || defaultVariant()?.id || '');
    while (this.selection.length < next) this.selection.push(fallback);
    this.selection.length = next;

    this.emit();
  },

  /**
   * @param {number} index Nulgebaseerde positie van het vest.
   * @param {string} variantId
   */
  setColour(index, variantId) {
    const variant = variantById(variantId);
    if (!variant || !variant.available) return;
    if (this.selection[index] === String(variantId)) return;

    this.selection[index] = String(variantId);
    this.emit();
  },

  /** Regels voor de cart: gelijke varianten worden één regel met hogere quantity. */
  get lines() {
    /** @type {Map<string, number>} */
    const grouped = new Map();
    for (const id of this.selection) {
      grouped.set(id, (grouped.get(id) || 0) + 1);
    }
    return [...grouped.entries()].map(([id, quantity]) => ({ id: Number(id), quantity }));
  },

  /**
   * Staffelkorting voor het huidige aantal. Deze waarde staat in de theme
   * settings en hoort gelijk te zijn aan de Shopify automatic discount.
   * @returns {number}
   */
  get tierPercent() {
    const tiers = window.KRData?.discounts || {};
    return Number(tiers[this.quantity] || 0);
  },

  /**
   * Totaal in centen. Per stuk afronden en daarna optellen, net zoals Shopify
   * de automatic discount per regel toepast.
   * @returns {number}
   */
  get totalCents() {
    const percent = this.tierPercent;
    return this.selection.reduce((sum, id) => {
      const price = variantById(id)?.price || 0;
      const unit = percent > 0 ? Math.round((price * (100 - percent)) / 100) : price;
      return sum + unit;
    }, 0);
  },

  /** @returns {number} Totale compare-at prijs in centen. */
  get totalCompareCents() {
    return this.selection.reduce((sum, id) => {
      const variant = variantById(id);
      return sum + (variant?.compare_at_price || variant?.price || 0);
    }, 0);
  },
};

/* ------------------------------------------------------- purchase panel -- */

class KrPurchase extends HTMLElement {
  /** @type {(() => void) | null} */
  #unsubscribe = null;
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    store.init();

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.#unsubscribe = store.subscribe((state) => this.#render(state));
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const quantityOption = target.closest('[data-kr-qty]');
    if (quantityOption instanceof HTMLElement) {
      store.setQuantity(Number(quantityOption.dataset.krQty));
      return;
    }

    const swatch = target.closest('[data-kr-variant]');
    if (swatch instanceof HTMLElement && !swatch.hasAttribute('disabled')) {
      const row = swatch.closest('[data-kr-row]');
      const index = row instanceof HTMLElement ? Number(row.dataset.krRow) : 0;
      store.setColour(index, String(swatch.dataset.krVariant));

      // Vest 1 heeft een beeld in de galerij hierboven. Alleen wanneer dit
      // koopvenster in de hero zelf staat (niet de afsluitende koopsectie)
      // springt de galerij mee naar die kleur, zodat kijken en kopen nooit
      // uit elkaar lopen zonder de pagina vanaf een ver koopvenster te
      // verplaatsen.
      if (index === 0 && this.closest('#kr-product')) {
        document.dispatchEvent(
          new CustomEvent('kr:show-colour-slide', { detail: { variantId: String(swatch.dataset.krVariant) } })
        );
      }
      return;
    }

    if (target.closest('[data-kr-submit]')) {
      event.preventDefault();
      this.#submit();
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    if (!select.matches('[data-kr-variant-select]')) return;

    const row = select.closest('[data-kr-row]');
    const index = row instanceof HTMLElement ? Number(row.dataset.krRow) : 0;
    store.setColour(index, select.value);
  };

  /** @param {{quantity: number, selection: string[]}} state */
  #render(state) {
    // Aantalkeuze
    for (const option of this.querySelectorAll('[data-kr-qty]')) {
      const isActive = Number(/** @type {HTMLElement} */ (option).dataset.krQty) === state.quantity;
      option.setAttribute('aria-checked', String(isActive));
      option.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    // Kleurrijen: exact zoveel rijen als vesten
    const rows = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-kr-row]')]);
    for (const row of rows) {
      const index = Number(row.dataset.krRow);
      row.hidden = index >= state.quantity;
    }

    const heading = this.querySelector('[data-kr-colour-heading]');
    if (heading) {
      heading.textContent =
        state.quantity === 1
          ? window.KRData?.strings?.chooseColour || 'Choose your colour'
          : window.KRData?.strings?.chooseColours || 'Choose your colours';
    }

    // Rijlabels alleen tonen bij meerdere vesten
    for (const label of this.querySelectorAll('[data-kr-rowlabel]')) {
      /** @type {HTMLElement} */ (label).hidden = state.quantity === 1;
    }

    // Actieve swatch per rij
    for (const swatch of this.querySelectorAll('[data-kr-variant]')) {
      const element = /** @type {HTMLElement} */ (swatch);
      const row = element.closest('[data-kr-row]');
      const index = row instanceof HTMLElement ? Number(row.dataset.krRow) : 0;
      const isActive = state.selection[index] === element.dataset.krVariant;
      element.setAttribute('aria-checked', String(isActive));
      element.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const select of this.querySelectorAll('[data-kr-variant-select]')) {
      const element = /** @type {HTMLSelectElement} */ (select);
      const row = element.closest('[data-kr-row]');
      const index = row instanceof HTMLElement ? Number(row.dataset.krRow) : 0;
      if (state.selection[index]) element.value = state.selection[index];
    }

    // Knoptekst en totaal
    const label = this.querySelector('[data-kr-submit-label]');
    if (label) {
      const template = window.KRData?.strings?.addToBag || 'Add [count] to bag';
      label.textContent = template.replace('[count]', String(state.quantity));
    }

    const total = this.querySelector('[data-kr-total]');
    if (total) total.textContent = formatMoney(store.totalCents);

    const totalWas = this.querySelector('[data-kr-total-was]');
    if (totalWas instanceof HTMLElement) {
      const compare = store.totalCompareCents;
      const showCompare = compare > store.totalCents;
      totalWas.hidden = !showCompare;
      if (showCompare) totalWas.textContent = formatMoney(compare);
    }

    // Staffelkorting benoemen, zodat het bedrag in de cart geen verrassing is.
    const saving = this.querySelector('[data-kr-saving]');
    if (saving instanceof HTMLElement) {
      const percent = store.tierPercent;
      saving.hidden = percent <= 0;
      if (percent > 0) {
        const template = window.KRData?.strings?.tierSaving || 'Save [percent]% — applied automatically in your bag';
        saving.textContent = template.replace('[percent]', String(percent));
      }
    }

    // Samenvatting voor de mobiele koopbalk
    const summary = this.querySelector('[data-kr-summary]');
    if (summary) {
      const names = state.selection.map((id) => variantById(id)?.title || '').filter(Boolean);
      summary.textContent = names.join(' · ');
    }
  }

  async #submit() {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-kr-submit]'));
    const errorSlot = this.querySelector('[data-kr-error]');
    const liveRegion = this.querySelector('[data-kr-live]');

    if (!button || button.disabled) return;

    button.disabled = true;
    if (errorSlot) errorSlot.textContent = '';

    // Vraag Shopify om de cart-secties opnieuw te renderen, zodat kortingen en
    // subtotaal uit Shopify komen en niet uit onze eigen berekening.
    const sectionIds = new Set();
    for (const node of document.querySelectorAll('cart-items-component')) {
      if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.add(node.dataset.sectionId);
    }

    try {
      const response = await fetch(themeGlobal()?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: store.lines,
          sections: [...sectionIds].join(','),
          sections_url: window.location.pathname,
        }),
      });

      const payload = await response.json();

      if (payload.status) {
        const message = payload.description || payload.message || '';
        if (errorSlot) errorSlot.textContent = message;
        document.dispatchEvent(
          new CartErrorEvent(this.id || 'kr-purchase', payload.message, payload.description, payload.errors)
        );
        return;
      }

      let cart = null;
      try {
        cart = await (await fetch(`${themeGlobal()?.routes?.cart_url || '/cart'}.js`)).json();
      } catch {
        cart = null;
      }

      const added = store.lines.reduce((sum, line) => sum + line.quantity, 0);
      if (liveRegion) {
        const template = window.KRData?.strings?.addedToBag || '[count] added to your bag';
        liveRegion.textContent = template.replace('[count]', String(added));
      }

      // De cart drawer luistert op document en opent zichzelf bij auto-open.
      document.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id || 'kr-purchase', {
          source: 'kr-purchase',
          itemCount: added,
          productId: window.KRData?.productId,
          sections: payload.sections,
        })
      );

      // Openen forceren, ook als auto-open uitstaat in de themasettings.
      const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('auto-open')) {
        drawer.open();
      }
    } catch (error) {
      console.error('[KORERIG] add to bag failed', error);
      if (errorSlot) {
        errorSlot.textContent = window.KRData?.strings?.genericError || 'Something went wrong. Please try again.';
      }
    } finally {
      button.disabled = false;
    }
  }
}

/* --------------------------------------------------------- colour section -- */

/** Kleursectie verderop op de pagina, gekoppeld aan hetzelfde koopmodel. */
class KrColourSection extends HTMLElement {
  /** @type {(() => void) | null} */
  #unsubscribe = null;
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    store.init();

    this.addEventListener(
      'click',
      (event) => {
        const option = /** @type {HTMLElement} */ (event.target).closest('[data-kr-variant]');
        if (option instanceof HTMLElement && !option.hasAttribute('disabled')) {
          store.setColour(0, String(option.dataset.krVariant));
        }
      },
      { signal: this.#controller.signal }
    );

    this.#unsubscribe = store.subscribe((state) => {
      for (const option of this.querySelectorAll('[data-kr-variant]')) {
        const element = /** @type {HTMLElement} */ (option);
        element.setAttribute('aria-checked', String(state.selection[0] === element.dataset.krVariant));
      }
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }
}

/* ---------------------------------------------------------------- gallery -- */

/**
 * Hoofdgalerij van de productpagina: één rij miniaturen — de productkleuren,
 * gevolgd door de goedgekeurde actie- en detailbeelden. Alleen Black kan de
 * <kr-turntable> bevatten; de andere kleuren zijn gewone stilstaande foto's,
 * zodat de 360°-bediening nooit een swipe op een andere kleur onderschept.
 *
 * De galerij is uitsluitend bedoeld om beelden te bekijken en wijzigt nooit
 * de bestelde variant. Een expliciete kleurklik in het koopvenster van deze
 * hero (`kr:show-colour-slide`, zie KrPurchase hierboven) laat de galerij
 * wel naar de gekozen kleur springen.
 *
 * Klonen (2026-09-10, urgente correctieronde — herbouwd): worden UITSLUITEND
 * hier in JS gebouwd, nooit in Liquid. Bij elke initialisatie:
 *   1. Verwijder eerst alle bestaande directe kloon-kinderen van de track
 *      (`[data-kr-clone="true"]`) — of ze nu van een vorige JS-run komen of
 *      per ongeluk nog server-side gerenderd zouden zijn.
 *   2. Lees de echte slides uitsluitend als directe kinderen van de track
 *      met een `data-kr-slide`-attribuut (`:scope > .kr-pgallery__slide
 *      [data-kr-slide]`) — nooit via de volledige hero, sectie of document,
 *      en nooit via section.blocks of een andere databron.
 *   3. Kloon de LAATSTE echte slide (`originalSlides.at(-1)`) en zet die
 *      vóóraan (voor soepel terugswipen); kloon de EERSTE echte slide
 *      (`originalSlides[0]`) en zet die achteraan (voor soepel doorswipen).
 *      Omdat elke kloon een cloneNode(true) van een al-gerenderde, echte
 *      slide is, kan een reviewafbeelding hier per definitie nooit in
 *      terechtkomen, ongeacht welke blocktypes de sectie verder krijgt.
 * De vorige/volgende-knoppen en het slepen/swipen raken de klonen zelf nooit
 * inhoudelijk aan; zodra scrollen op een kloon tot stilstand komt, springt
 * #handleSettle onzichtbaar terug naar de overeenkomstige echte slide.
 *
 * Geen dynamische viewporthoogte meer: `.kr-pgallery__viewport` houdt altijd
 * de vaste `aspect-ratio: 1/1` uit de stylesheet aan, voor elke slide gelijk
 * — dus geen layout shift bij het wisselen van slide.
 */
class KrGallery extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /**
   * Gecachete, stabiele breedte van <kr-gallery> zelf — gebruikt voor de
   * scroll-wiskunde in #goTo/#handleSettle, zodat die nooit een tussentijds
   * krimpende/groeiende breedte als basis pakt.
   * @type {number}
   */
  #galleryWidth = 0;
  /** Echte slides, in DOM-volgorde, ná het bouwen van de klonen op DOM-posities 1..N. */
  /** @type {HTMLElement[]} */
  #realSlides = [];
  #activeIndex = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const track = this.querySelector('[data-kr-track]');
    if (!(track instanceof HTMLElement)) return;
    this.#track = track;

    this.#galleryWidth = this.clientWidth;
    this.#rebuildClones();

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);

        const thumb = target.closest('[data-kr-thumb]');
        if (thumb instanceof HTMLElement) {
          this.#goTo(Number(thumb.dataset.krThumb));
          return;
        }

        if (target.closest('[data-kr-prev]')) {
          this.#goTo(this.#activeIndex - 1);
          return;
        }

        if (target.closest('[data-kr-next]')) {
          this.#goTo(this.#activeIndex + 1);
        }
      },
      { signal }
    );

    // Enige bron van waarheid voor "waar is er handmatig heen gesleept/geswiped":
    // de daadwerkelijke, tot stilstand gekomen scrollpositie. scrollend vuurt
    // precies één keer wanneer de browser (en de scroll-snap) klaar zijn.
    track.addEventListener('scrollend', () => this.#handleSettle(), { signal });

    if (!('onscrollend' in window)) {
      // Oudere browsers zonder scrollend (bv. Safari < 18): gedebouncete
      // scroll-fallback die hetzelfde meet zodra het scrollen stil ligt.
      let settleTimer;
      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(settleTimer);
          settleTimer = setTimeout(() => this.#handleSettle(), 120);
        },
        { signal, passive: true }
      );
    }

    document.addEventListener(
      'kr:show-colour-slide',
      (event) => {
        const variantId = /** @type {CustomEvent} */ (event).detail?.variantId;
        const index = this.#realSlides.findIndex((slide) => slide.dataset.krVariant === variantId);
        if (index >= 0) this.#goTo(index);
      },
      { signal }
    );

    // Herberekent #galleryWidth zodra <kr-gallery> zélf daadwerkelijk van
    // breedte verandert (venster-resize, rotatie, grid-herschikking).
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        const width = this.clientWidth;
        if (width > 0 && width !== this.#galleryWidth) {
          this.#galleryWidth = width;
        }
      });
      this.#resizeObserver.observe(this);
      signal.addEventListener('abort', () => this.#resizeObserver?.disconnect());
    }

    // Startpositie: DOM-positie 1 (de eerste echte slide), niet 0 (de leading
    // clone). De klonen staan op dit punt al vast dankzij #rebuildClones
    // hierboven, dus deze meting en toewijzing gebeuren gegarandeerd ná het
    // bouwen van de klonen, in dezelfde synchrone initialisatie.
    const width = this.#galleryWidth || track.clientWidth;
    if (width) track.scrollLeft = width;
    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Verwijdert alle bestaande klonen en bouwt ze opnieuw op, uitsluitend uit
   * de op dat moment aanwezige echte slides. Zie de klassecommentaar.
   */
  #rebuildClones() {
    const track = this.#track;
    if (!track) return;

    for (const clone of track.querySelectorAll(':scope > [data-kr-clone="true"]')) {
      clone.remove();
    }

    const originalSlides = /** @type {HTMLElement[]} */ ([
      ...track.querySelectorAll(':scope > .kr-pgallery__slide[data-kr-slide]'),
    ]);

    if (originalSlides.length === 0) {
      this.#realSlides = [];
      return;
    }

    const makeClone = (source) => {
      const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
      clone.removeAttribute('data-kr-slide');
      clone.removeAttribute('data-kr-variant');
      clone.setAttribute('data-kr-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      return clone;
    };

    const leadingClone = makeClone(originalSlides[originalSlides.length - 1]);
    track.insertBefore(leadingClone, track.firstChild);

    const trailingClone = makeClone(originalSlides[0]);
    track.appendChild(trailingClone);

    this.#realSlides = originalSlides;
  }

  /**
   * Springt rechtstreeks naar een echte slide (nooit een kloon), altijd
   * modulo het aantal echte slides — vorige vanaf index 0 komt dus uit op de
   * laatste index, en andersom. Werkt de status synchroon bij in plaats van
   * te wachten op scrollend: een klik op een miniatuur of pijl moet meteen
   * zichtbaar effect hebben, ook bij snel achter elkaar klikken. Instant
   * scrollen (niet smooth): smooth botst op sommige browsers met
   * scroll-snap op de track en scrollt dan helemaal niet.
   * @param {number} index
   */
  #goTo(index) {
    const track = this.#track;
    if (!track) return;
    const total = this.#realSlides.length || 1;
    const clamped = ((index % total) + total) % total;
    const width = this.#galleryWidth || track.clientWidth || 1;
    track.scrollLeft = (clamped + 1) * width;
    this.#setActive(clamped);
  }

  /**
   * Wordt aangeroepen zodra de track daadwerkelijk tot stilstand is gekomen
   * (handmatig slepen/swipen; knop- en miniatuurklikken lopen via #goTo).
   * Komt de gebruiker op een kloon uit, dan springt dit onzichtbaar (geen
   * animatie, gewoon scrollLeft zetten) naar de overeenkomstige echte slide.
   */
  #handleSettle() {
    const track = this.#track;
    if (!track) return;
    const total = this.#realSlides.length;
    if (total === 0) return;

    const width = this.#galleryWidth || track.clientWidth || 1;
    const pos = Math.round(track.scrollLeft / width);

    if (pos <= 0) {
      // Kloon van de laatste slide (vóór de eerste echte slide) → echte laatste slide.
      track.scrollLeft = total * width;
      this.#setActive(total - 1);
      return;
    }

    if (pos >= total + 1) {
      // Kloon van de eerste slide (ná de laatste echte slide) → echte eerste slide.
      track.scrollLeft = 1 * width;
      this.#setActive(0);
      return;
    }

    const logicalIndex = Math.max(0, Math.min(total - 1, pos - 1));
    this.#setActive(logicalIndex);
  }

  /**
   * @param {number} index
   */
  #setActive(index) {
    this.#activeIndex = index;

    for (const thumb of this.querySelectorAll('[data-kr-thumb]')) {
      const element = /** @type {HTMLElement} */ (thumb);
      element.setAttribute('aria-current', String(Number(element.dataset.krThumb) === index));
    }

    // Alleen een expliciete keuze in de koopmodule mag de bestelde variant wijzigen.
  }
}

/* ------------------------------------------------------------- highlights -- */

class KrHighlights extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const track = this.querySelector('[data-kr-track]');
    if (!(track instanceof HTMLElement)) return;

    const slides = [...track.children];

    this.addEventListener(
      'click',
      (event) => {
        const dot = /** @type {HTMLElement} */ (event.target).closest('[data-kr-dot]');
        if (!(dot instanceof HTMLElement)) return;

        slides[Number(dot.dataset.krDot)]?.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      },
      { signal }
    );

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = slides.indexOf(/** @type {Element} */ (entry.target));
          for (const dot of this.querySelectorAll('[data-kr-dot]')) {
            const element = /** @type {HTMLElement} */ (dot);
            element.setAttribute('aria-current', String(Number(element.dataset.krDot) === index));
          }
        }
      },
      { root: track, threshold: 0.55 }
    );

    for (const slide of slides) observer.observe(slide);
    signal.addEventListener('abort', () => observer.disconnect());
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }
}

/* ------------------------------------------------------------ media slot -- */

/**
 * Speelt een vooraf gerenderde animatie precies één keer af zodra de sectie in
 * beeld komt. Bij reduced motion blijft alleen de poster staan.
 */
class KrMedia extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    if (prefersReducedMotion()) {
      video.remove();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          video.dataset.playing = 'true';
          video.play().catch(() => {
            // Autoplay geweigerd: de poster blijft staan, dat is een prima uitkomst.
            video.dataset.playing = 'false';
          });
          this.#observer?.disconnect();
          this.#observer = null;
        }
      },
      { threshold: 0.35 }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

/* ------------------------------------------------------------- accordion -- */

class KrAccordion extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();

    this.addEventListener(
      'click',
      (event) => {
        const button = /** @type {HTMLElement} */ (event.target).closest('[data-kr-acc-btn]');
        if (!(button instanceof HTMLButtonElement)) return;

        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));

        const panel = document.getElementById(button.getAttribute('aria-controls') || '');
        if (panel) panel.dataset.open = String(!expanded);
      },
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }
}

/* -------------------------------------------- sticky nav + mobile buy bar -- */

class KrStickyBuy extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const anchor = document.querySelector('[data-kr-buy-anchor]');
    if (!anchor) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.dataset.visible = String(!entry.isIntersecting);
      },
      { rootMargin: '-72px 0px 0px 0px', threshold: 0 }
    );

    this.#observer.observe(anchor);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

/* --------------------------------------------------------- scroll reveal -- */

function initReveal() {
  const targets = document.querySelectorAll('.kr-reveal:not([data-shown])');
  if (targets.length === 0) return;

  if (prefersReducedMotion()) {
    for (const target of targets) /** @type {HTMLElement} */ (target).dataset.shown = 'true';
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        /** @type {HTMLElement} */ (entry.target).dataset.shown = 'true';
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  for (const target of targets) observer.observe(target);
}

/* ------------------------------------------------------- smooth anchoring -- */

function initAnchors() {
  if (window.KRData?.anchorsBound) return;
  window.KRData = window.KRData || {};
  window.KRData.anchorsBound = true;

  document.addEventListener('click', (event) => {
    const link = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-kr-scroll]');
    if (!(link instanceof HTMLElement)) return;

    const selector = link.getAttribute('href') || link.dataset.krScroll;
    if (!selector || !selector.startsWith('#')) return;

    const destination = document.querySelector(selector);
    if (!destination) return;

    event.preventDefault();
    destination.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  });
}

/* ------------------------------------------------------------- registratie -- */

const elements = /** @type {const} */ ([
  ['kr-purchase', KrPurchase],
  ['kr-colour-section', KrColourSection],
  ['kr-gallery', KrGallery],
  ['kr-highlights', KrHighlights],
  ['kr-media', KrMedia],
  ['kr-accordion', KrAccordion],
  ['kr-sticky-buy', KrStickyBuy],
]);

for (const [name, constructor] of elements) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}

initAnchors();
initReveal();

// Shopify theme editor: secties worden opnieuw gerenderd, dus opnieuw binden.
document.addEventListener('shopify:section:load', initReveal);
